import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import { useMaintenanceMode } from '@askporter/api-client';
import MaintenancePage from '../../pages/MaintenancePage';

interface Props {
  children: React.ReactNode[] | React.ReactNode;
}

/**
 * The MaintenanceModeDetector component aims to capture ALL Grieg 503s for requests made using Axios. The main known
 * exclusion is calls to the static/tenant made in the ConfigProvider, these fallback to a static maintenance page and
 * are triggered by the ConfigProvider.
 */
const MaintenanceModeDetector: React.FC<React.PropsWithChildren<Props>> = ({ children }: Props) => {
  const queryClient = useQueryClient();

  /**
   * Data read during a window can be stale by the time it ends: writes were
   * refused, and derived data lags until it converges. Nothing refetches on its
   * own here — `refetchOnWindowFocus` and `refetchOnReconnect` are both off in
   * this app's query client config — so the window ending is the signal to
   * invalidate everything and let mounted queries refetch (NS-2462).
   *
   * This matters most when the takeover never showed. A read-only window keeps
   * the app mounted behind the banner, so its queries survive the whole window;
   * a takeover unmounts them and `gcTime: 0` discards them anyway.
   */
  const handleWindowEnded = React.useCallback(() => {
    void queryClient.invalidateQueries();
  }, [queryClient]);

  const isMaintenanceMode = useMaintenanceMode(handleWindowEnded);

  return isMaintenanceMode ? (
    <BrowserRouter>
      <MaintenancePage />
    </BrowserRouter>
  ) : (
    <>{children}</>
  );
};

export default MaintenanceModeDetector;
